import {_} from 'lodash';
import * as ko from "knockout";
import {ILanguageObject, TLanguageKey} from "./constants/Language";
import {global, lm} from "./appContext";
import {getEadmRole, getPermissionDefinition, getSyncFieldValue} from "./util/misc";
import {Permission} from "./constants/Permission";
import {nsLanguage} from "./util/nsLanguage";
import {nsAmplify} from "./util/nsAmplify";
import {CHECK_FOR_VERSION_THROTTLE_DURATION} from "./constants";
import getLanguageProperty = nsLanguage.getLanguageProperty;

export default class Config {

    public BUILD_DATE!: string
    public BUILD_VERSION!: string
    public DEBUG!: boolean

    // Note: don't access these members directly, use getWebServiceUrl() instead
    private URL_IDENTITY_HUB!: Record<string, string>
    private URL_PREFIX!: Record<string, string>
    private URL_FILE!: Record<string, string>

    public URL_FEEDBACK!: ILanguageObject
    public URL_SUPPORT!: ILanguageObject
    public URL_DOCUMENTATION!: ILanguageObject
    public URL_ACCESSIBILITY_STATEMENT!: ILanguageObject

    /**
     * Innhold i iframe på innloggingssiden (høyre panel), per språk (samme mønster som {@link URL_DOCUMENTATION}).
     * Oppslagsrekkefølge som for dokumentasjon/tilbakemelding: valgt språk, deretter `en`, deretter `nb`. Sett `en` som reserve.
     * Full URL; cookie-banner i rammen håndteres på innholdsvert, ikke via CSP/sandbox her.
     */
    public URL_LOGIN_MARKETING?: ILanguageObject | string

    public RECAPTCHA_SITE_KEY!: string

    public HUBSPOT_SCRIPT_URL!: string

    public AVAILABLE_LANGUAGES!: TLanguageKey[]
    public DEFAULT_LANGUAGE: TLanguageKey = 'en'

    /** Dynamic variable autocomplete: items loaded per scroll/page interval. */
    public DYNAMIC_VARIABLE_LIST_PAGE_SIZE: number = 40
    /** Load next interval when scroll passes this many items before the end of the loaded list. */
    public DYNAMIC_VARIABLE_LIST_SCROLL_THRESHOLD: number = 20

    public readonly isLoaded: KnockoutObservable<boolean> = ko.observable(false)
    private checkForVersionTimeoutId!: number
    public isNewVersion: boolean | null = null

    init(): Promise<void> {
        if (this.isLoaded()) {
            return Promise.resolve()
        }
        const url = 'config.json?t=' + new Date().getTime()
        return fetch(url)
            .then((response: Response) =>
                response
                    .json()
                    .then(config => {
                        _.extend(this, config)

                        












                        // store the current build version so that we can compare it in the future (and take appropriate actions)
                        nsAmplify.store("buildversion", this.BUILD_VERSION)
                        this.isLoaded(true)
                    })
            )
    }

    public checkForNewVersion() {
        clearTimeout(this.checkForVersionTimeoutId)
        this.checkForVersionTimeoutId = setTimeout(() => this._checkForNewVersion(), CHECK_FOR_VERSION_THROTTLE_DURATION);
    }

    private _checkForNewVersion() {
        const url = 'config.json?t=' + new Date().getTime()
        return fetch(url)
            .then((response: Response) =>
                response
                    .json()
                    .then(config => {
                        this.isNewVersion = config.BUILD_VERSION != this.BUILD_VERSION
                        if (this.isNewVersion) {
                            const message = lm.t(`((New eAdm version is available|version:${config.BUILD_VERSION})) <a href="javascript:window.location.reload()">((Click here to reload application))</a>`)
                            global.messageData({
                                message: message ?? '',
                                type: 'success',
                            })
                        } else {
                            this.checkForNewVersion()
                        }
                    })
            )
            .catch(reason => {
                // die silently
                this.checkForNewVersion()
            })
    }

    private getHref_documentation(): string {
        const url = new URL(getLanguageProperty(this.URL_DOCUMENTATION))
        return url.toString()
    }

    private getHref_accessibility(): string {
        const url = new URL(getLanguageProperty(this.URL_ACCESSIBILITY_STATEMENT))
        return url.toString()
    }

    private getHref_feedback(): string {
        // trigger knockout binding
        const currentRoute = global.router.currentRoute()

        const url = new URL(getLanguageProperty(this.URL_FEEDBACK))
        if (!global.login.isLoggedIn()) {
            return url.toString()
        }
        const user = global.login.user();
        if (!user) {
            return url.toString()
        }
        const params: [string, string][] = [
            ['email', user.email],
            ['firstname', getSyncFieldValue('GivenName', user.syncfields)],
            ['lastname', getSyncFieldValue('Surname', user.syncfields)],
            ['jobtitle', getPermissionDefinition(getEadmRole() ?? Permission.None).serverName ?? ''],
            ['origin_url_when_form_submitted', window.location.toString()],
            ['eadm_user_id', user.id.toString()],
            ['company', global.login.getHomeOrg()?.organizationame ?? ''],
        ]
        this.addSearchParams(url, params)
        return url.toString()
    }

    private getHref_support(): string {
        // trigger knockout binding
        const currentRoute = global.router.currentRoute()

        const url: URL = new URL(getLanguageProperty(this.URL_SUPPORT))
        if (!global.login.isLoggedIn()) {
            return url.toString()
        }
        const user = global.login.user();
        if (!user) {
            return url.toString()
        }
        const homeOrg = global.login.getHomeOrg();
        const params: [string, string][] = [
            ['product', 'eADM'],
            ['email', user.email],
            ['company', homeOrg?.organizationame ?? ''],
            ['origin_url_when_form_submitted', window.location.toString()],
            ['organisation_number', homeOrg?.organizationid ?? ''],
        ]
        this.addSearchParams(url, params)
        return url.toString()

    }

    private addSearchParams(url: URL, params: [string, string][]): void {
        params.forEach(tuple => url.searchParams.set(tuple[0], tuple[1]))
    }

    public getUrlPrefix(): string {
        return this.getTopLevelDomainString(this.URL_PREFIX)
    }

    public getUrlFile(): string {
        return this.getTopLevelDomainString(this.URL_FILE)
    }

    public getUrlIdentityHub(): string {
        return this.getTopLevelDomainString(this.URL_IDENTITY_HUB)
    }

    private getTopLevelDomainString(record: Record<string, string>): string {
        const tld = _.last(window.location.hostname.split('.'))
        if (tld && tld in record) {
            return record[tld] ?? ''
        }
        return record['_default'] ?? ''
    }

}